import { useHotelPolicyConfig } from "@app/hooks/use-policy";
import { PolicyDataField } from "./policy-data-field";
import { PolicyFormActions } from "./policy-form-actions";
import { PolicyPageError } from "./policy-table-header";
import { PolicySection } from "./policy-section";
import { TableLoadingOverlay } from "@its-engineering-and-development/ui-foundation/components";

export function HotelPolicyPage() {
    const {
        config,
        hasChanges,
        isLoading,
        isSaving,
        error,
        updateRecommendedField,
        updateAdditionalField,
        updateVatField,
        setOutsideHomeCountry,
        updatePersonalNonReimbursable,
        cancelChanges,
        saveChanges,
    } = useHotelPolicyConfig();

    const vatSectionDisabled = !config.outsideHomeCountry;
    const showOverlay = isLoading || isSaving;

    return (
        <div className="relative rounded-xl border border-gray-200 bg-white shadow-sm">
            <TableLoadingOverlay isLoading={showOverlay} label={isSaving ? "Saving hotel policy…" : "Loading hotel policy…"} />

            <div className="flex flex-col gap-3 border-b border-gray-200 px-6 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Hotel Policy</h1>
                    <p className="mt-1 text-sm text-gray-500">Activate detail for hotel data collection.</p>
                </div>

                <PolicyFormActions hasChanges={hasChanges} isSaving={isSaving} onCancel={cancelChanges} onSave={() => void saveChanges()} />
            </div>

            {error && (
                <div className="px-6 pt-4 sm:px-8">
                    <PolicyPageError message={error} />
                </div>
            )}

            <div className="flex flex-col gap-8 px-6 py-8 sm:px-8">
                <PolicySection
                    title="Recommended Hotel Data to Collect"
                    description="These fields are recommended for hotel expense capture, as companies track taxes and set policies for hotel spend tied to 'Room Rate.'"
                    showDivider={false}
                >
                    <div className="flex flex-col gap-8">
                        {config.recommendedFields.map((field) => (
                            <PolicyDataField
                                key={field.id}
                                label={field.label}
                                fieldType={field.fieldType}
                                requirement={field.requirement}
                                enabled={field.enabled}
                                onEnabledChange={(enabled) => updateRecommendedField(field.id, { enabled })}
                                description={field.description}
                                onDescriptionChange={(description) => updateRecommendedField(field.id, { description })}
                                showDescription
                            />
                        ))}
                    </div>
                </PolicySection>

                <PolicySection
                    title="Additional Hotel Data to Consider"
                    description="Enable these fields if you need to collect additional hotel charge details."
                >
                    <div className="grid gap-8 md:grid-cols-2">
                        {config.additionalFields.map((field) => (
                            <PolicyDataField
                                key={field.id}
                                label={field.label}
                                fieldType={field.fieldType}
                                requirement={field.requirement}
                                enabled={field.enabled}
                                onEnabledChange={(enabled) => updateAdditionalField(field.id, { enabled })}
                            />
                        ))}
                    </div>
                </PolicySection>

                <PolicySection
                    title="Personal & Non-Reimbursable Expenses"
                    description="Collect personal expenses that should not be reimbursed (e.g., mini-bar, movies, spa services)."
                >
                    <PolicyDataField
                        label={config.personalNonReimbursable.label}
                        fieldType={config.personalNonReimbursable.fieldType}
                        requirement={config.personalNonReimbursable.requirement}
                        enabled={config.personalNonReimbursable.enabled}
                        onEnabledChange={(enabled) => updatePersonalNonReimbursable({ enabled })}
                        description={config.personalNonReimbursable.description}
                        onDescriptionChange={(description) => updatePersonalNonReimbursable({ description })}
                        showDescription
                    />
                </PolicySection>

                <PolicySection title="International Travel" description="Track international hotel stays to enable VAT and tax ID collection.">
                    <div className="flex flex-col gap-6">
                        <PolicyDataField
                            label="Outside Home Country?"
                            enabled={config.outsideHomeCountry}
                            onEnabledChange={setOutsideHomeCountry}
                            showMetadata={false}
                            hint={
                                <>
                                    Checkbox <span className="text-blue-600">Triggers VAT section</span>
                                </>
                            }
                        />

                        <hr className="border-gray-200" />

                        <div>
                            <h3 className={`text-sm font-semibold ${vatSectionDisabled ? "text-gray-400" : "text-gray-900"}`}>VAT Information</h3>
                            <p className={`mt-1 min-h-5 text-sm ${vatSectionDisabled ? "text-gray-400" : "invisible"}`}>
                                Enable &ldquo;Outside Home Country?&rdquo; to activate VAT information collection.
                            </p>

                            <div className="mt-6 flex flex-col gap-8">
                                {config.vatFields.map((field) => (
                                    <PolicyDataField
                                        key={field.id}
                                        label={field.label}
                                        fieldType={field.fieldType}
                                        requirement={field.requirement}
                                        enabled={field.enabled}
                                        onEnabledChange={(enabled) => updateVatField(field.id, { enabled })}
                                        isDisabled={vatSectionDisabled}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </PolicySection>

                <PolicyFormActions
                    hasChanges={hasChanges}
                    isSaving={isSaving}
                    onCancel={cancelChanges}
                    onSave={() => void saveChanges()}
                    className="border-t border-gray-200 pt-6"
                />
            </div>
        </div>
    );
}
